import { useMemo } from 'react';
import { isNil, isNumber, first, flatMap, groupBy, sortBy, values, meanBy, uniqBy } from 'lodash';

import { isValidCoordinate, isValidCorners } from 'utils/latLng';
import { isSolarPictureTarget } from 'horizon/components/Images/ImageViewer/utils';
import {
  type BladeTarget,
  type TowerOrTransitionPieceTarget,
  type PictureTarget,
  type FullscreenImageQueryPicture,
} from 'horizon/routes/Inspections/types';
import {
  type AtlasGqlPicture,
  type AtlasGqlInspection,
  type AtlasGqlTargetBlade_External,
  type AtlasGqlTargetBlade_Internal,
  type AtlasGqlTargetTower_External,
  type AtlasGqlTargetTransition_Piece,
  type AtlasGqlTargetNacelle_External,
  type AtlasGqlTargetGearbox,
  type AtlasGqlTargetSolar,
  type AtlasGqlTargetGeneric,
  type AtlasGqlTurbine,
  type AtlasGqlBlade,
  type AtlasGqlGenericAsset,
  type AtlasGqlHorizonDamageObservation,
  useGetRelatedPicturesQuery,
} from 'types/atlas-graphql';

const METERS_PER_DEGREE_LATITUDE = 111320;
const RELATED_PICTURES_GPS_BOUNDARY_SIDE_METERS = 50;

const getSquareGpsBoundary = (
  gpsPosition?: { latitudeDegrees?: number | null; longitudeDegrees?: number | null } | null
): number[][] | undefined => {
  const latitude = gpsPosition?.latitudeDegrees;
  const longitude = gpsPosition?.longitudeDegrees;

  // return early for nil lat/long values
  if (isNil(latitude) || isNil(longitude)) {
    return undefined;
  }

  // return early for invalid lat/long values
  if (!isValidCoordinate(latitude) || !isValidCoordinate(longitude)) {
    return undefined;
  }

  const halfSideMeters = RELATED_PICTURES_GPS_BOUNDARY_SIDE_METERS / 2;
  const latOffset = halfSideMeters / METERS_PER_DEGREE_LATITUDE;
  const cosLatitude = Math.cos((latitude * Math.PI) / 180);
  const lonMetersPerDegree = METERS_PER_DEGREE_LATITUDE * Math.max(Math.abs(cosLatitude), 1e-6);
  const lonOffset = halfSideMeters / lonMetersPerDegree;

  return [
    [latitude + latOffset, longitude - lonOffset],
    [latitude + latOffset, longitude + lonOffset],
    [latitude - latOffset, longitude + lonOffset],
    [latitude - latOffset, longitude - lonOffset],
  ];
};

// get the average distance/height from an array of observations
const getAverageObservationAttribute = (
  observations: AtlasGqlHorizonDamageObservation[],
  attribute: 'Distance' | 'Height'
): number | undefined => {
  const filteredObservations = observations.filter(({ attrs }) => isNumber(attrs?.[attribute]));
  return filteredObservations.length > 0 ? meanBy(observations, `attrs.${attribute}`) : undefined;
};

// sorts pictures by distance/height and then groups them
const sortAndGroupPictures = (
  pictures: AtlasGqlPicture[],
  targetType: string,
  refDistance: number,
  refHeight: number
): AtlasGqlPicture[] => {
  // sort pictures by distance/height from a reference distance/height
  // note: pictures that have zero distance/height are sorted after those with valid values
  const sortedPictures = sortBy(pictures, picture => {
    const { target, horizonDamageObservations } = picture;
    const observations = (horizonDamageObservations ?? []) as AtlasGqlHorizonDamageObservation[];

    // using distance
    if (['BLADE_EXTERNAL', 'BLADE_INTERNAL'].includes(targetType)) {
      const picDistance = (target as BladeTarget)?.radialDistance;
      const obsDistance = getAverageObservationAttribute(observations, 'Distance');
      const distance = picDistance || obsDistance || undefined;

      if (isNumber(distance)) {
        return Math.abs(refDistance - distance);
      }
    }

    // using height
    if (['TOWER_EXTERNAL', 'TRANSITION_PIECE'].includes(targetType)) {
      const picHeight = (target as TowerOrTransitionPieceTarget)?.height;
      const obsHeight = getAverageObservationAttribute(observations, 'Height');
      const height = picHeight || obsHeight || undefined;

      if (isNumber(height)) {
        return Math.abs(refHeight - height);
      }
    }

    // default case
    return undefined;
  });

  // group pictures by blade for blade inspections
  const groups = values(
    groupBy(sortedPictures, picture => {
      const { target } = picture;

      if (targetType === 'BLADE_EXTERNAL') {
        const bladeTarget = target as AtlasGqlTargetBlade_External;
        const bladeLabel = bladeTarget.bladeAsset?.label;

        if (bladeLabel) {
          return bladeLabel;
        }
      }

      if (targetType === 'BLADE_INTERNAL') {
        const bladeTarget = target as AtlasGqlTargetBlade_Internal;
        const bladeLabel = bladeTarget.bladeAsset?.label;

        if (bladeLabel) {
          return bladeLabel;
        }
      }

      return picture;
    })
  );

  // return the first of each group
  return flatMap(groups.map(g => first(g)!));
};

// only inspections that came back with pictures that include the current
// picture's target asset id should be returned.
const pictureMatchesAsset = (
  picture: AtlasGqlPicture,
  targetType: string,
  assetId: string
): boolean => {
  const { target } = picture;

  if (targetType === 'BLADE_EXTERNAL' || targetType === 'BLADE_INTERNAL') {
    return (target as BladeTarget)?.bladeAsset?.id === assetId;
  }
  if (targetType === 'TOWER_EXTERNAL') {
    return (target as AtlasGqlTargetTower_External)?.tower?.id === assetId;
  }
  if (targetType === 'TRANSITION_PIECE') {
    return (target as AtlasGqlTargetTransition_Piece)?.transitionPiece?.id === assetId;
  }
  if (targetType === 'NACELLE_EXTERNAL') {
    return (target as AtlasGqlTargetNacelle_External)?.nacelle?.id === assetId;
  }
  if (targetType === 'GEARBOX') {
    return (target as AtlasGqlTargetGearbox)?.gearbox?.id === assetId;
  }
  if (targetType === 'SOLAR') {
    return (target as AtlasGqlTargetSolar)?.asset?.id === assetId;
  }
  if (targetType === 'GENERIC') {
    return (target as AtlasGqlTargetGeneric)?.asset?.id === assetId;
  }

  return false;
};

type UseRelatedPicturesProps = {
  picture: AtlasGqlPicture | FullscreenImageQueryPicture | null | undefined;
};

export const useRelatedPictures = ({
  picture,
}: UseRelatedPicturesProps): {
  loading: boolean;
  pictures: AtlasGqlPicture[];
  inspections: AtlasGqlInspection[];
} => {
  const target = picture?.target;
  const targetType = (target as PictureTarget)?.type;

  // assets
  const bladeAsset = (target as BladeTarget)?.bladeAsset;
  const tower = (target as AtlasGqlTargetTower_External)?.tower;
  const transitionPiece = (target as AtlasGqlTargetTransition_Piece)?.transitionPiece;
  const nacelle = (target as AtlasGqlTargetNacelle_External)?.nacelle;
  const gearbox = (target as AtlasGqlTargetGearbox)?.gearbox;
  const solar = (target as AtlasGqlTargetSolar)?.asset;
  const generic = (target as AtlasGqlTargetGeneric)?.asset;
  const asset = bladeAsset ?? tower ?? transitionPiece ?? nacelle ?? gearbox ?? solar ?? generic;
  const assetId = asset?.id;

  // variables
  const { id: inspectionId } = picture?.inspection ?? {};
  const bladeLabel = bladeAsset?.label;
  const bladeSide = (target as BladeTarget)?.bladeSide;
  const bladeChamber = (target as AtlasGqlTargetBlade_Internal)?.bladeChamber;
  const towerSide = (target as AtlasGqlTargetTower_External)?.towerSide;
  const transitionPieceSide = (target as AtlasGqlTargetTransition_Piece)?.transitionPieceSide;
  const nacelleSide = (target as AtlasGqlTargetNacelle_External)?.nacelleSide;
  const isSolarInspectionPicture = isSolarPictureTarget(target) && !!solar;
  const gpsBoundary = isSolarInspectionPicture
    ? isValidCorners((picture?.corners ?? []) as [number, number][])
      ? picture?.corners
      : getSquareGpsBoundary(picture?.gpsPosition)
    : undefined;

  // distance and height
  const observations = (picture?.horizonDamageObservations ??
    []) as AtlasGqlHorizonDamageObservation[];

  const picDistance = (target as BladeTarget)?.radialDistance;
  const obsDistance = getAverageObservationAttribute(observations, 'Distance');
  const refDistance = picDistance || obsDistance || 0;

  const picHeight = (target as TowerOrTransitionPieceTarget)?.height;
  const obsHeight = getAverageObservationAttribute(observations, 'Height');
  const refHeight = picHeight || obsHeight || 0;

  const variables = {
    id: inspectionId,
    bladeLabel,
    bladeSide,
    bladeChamber,
    towerSide,
    transitionPieceSide,
    nacelleSide,
    gpsBoundary,
  };

  // query for related pictures
  const { data: relatedPicturesData = {}, loading: relatedPicturesLoading } =
    useGetRelatedPicturesQuery({
      // @ts-ignore
      variables,
      skip: isNil(inspectionId),
    });

  // pictures
  const pictures = useMemo(() => {
    const pictures = (relatedPicturesData.inspection?.pictures ?? []) as AtlasGqlPicture[];

    return sortAndGroupPictures(pictures, targetType, refDistance, refHeight);
  }, [relatedPicturesData.inspection?.pictures, targetType, refDistance, refHeight]);

  // inspections
  const inspections = useMemo(() => {
    const inspectionAsset = relatedPicturesData.inspection?.asset as
      | AtlasGqlTurbine
      | AtlasGqlBlade
      | AtlasGqlGenericAsset
      | undefined;

    const assetInspections = (inspectionAsset?.inspections?.nodes ?? []) as AtlasGqlInspection[];

    // if the inspection is on a Turbine, also include inspections from its child Blades
    const childBladeInspections =
      inspectionAsset?.__typename === 'Turbine'
        ? (inspectionAsset.children ?? []).flatMap(
            child => ((child as AtlasGqlBlade)?.inspections?.nodes ?? []) as AtlasGqlInspection[]
          )
        : [];

    // if the inspection is on a Blade, also include inspections from its parent Turbine
    const parentTurbineInspections =
      inspectionAsset?.__typename === 'Blade'
        ? (((inspectionAsset.parent as AtlasGqlTurbine)?.inspections?.nodes ??
            []) as AtlasGqlInspection[])
        : [];

    // combined inspections and deduplicate
    const allInspections = uniqBy(
      [...assetInspections, ...childBladeInspections, ...parentTurbineInspections],
      'id'
    );

    // only inspections that came back with pictures that include the current
    // picture's target asset id should be returned.
    return allInspections.reduce<AtlasGqlInspection[]>((acc, inspection) => {
      // filter pictures to only include those of the same asset as the current picture
      const filteredPictures = (inspection.pictures ?? []).filter(
        picture => picture && pictureMatchesAsset(picture, targetType, assetId)
      ) as AtlasGqlPicture[];

      if (!filteredPictures.length) {
        return acc;
      }

      return [
        ...acc,
        {
          ...inspection,
          pictures: sortAndGroupPictures(filteredPictures, targetType, refDistance, refHeight),
        },
      ];
    }, []);
  }, [relatedPicturesData.inspection, targetType, assetId, refDistance, refHeight]);

  if (isNil(inspectionId)) {
    return {
      loading: false,
      pictures: [],
      inspections: [],
    };
  }

  return {
    loading: relatedPicturesLoading,
    pictures,
    inspections,
  };
};
